import { useState, useCallback, useEffect, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { 
  Loader2, Home, Euro, AlertTriangle, CheckCircle, 
  Building, RefreshCw, Check, X, Archive, Clock, Save, PlusCircle, MinusCircle, Filter
} from "lucide-react";
import { useTranslation } from "../contexts/I18nContext";

// Costanti
const MESI = [
  { value: "01", label: "Gennaio" },
  { value: "02", label: "Febbraio" },
  { value: "03", label: "Marzo" },
  { value: "04", label: "Aprile" },
  { value: "05", label: "Maggio" },
  { value: "06", label: "Giugno" },
  { value: "07", label: "Luglio" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Settembre" },
  { value: "10", label: "Ottobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Dicembre" }
];

// Genera lista anni (5 anni indietro, 2 avanti)
const currentYear = new Date().getFullYear();
const ANNI = Array.from({ length: 8 }, (_, i) => currentYear - 5 + i).map(y => ({ value: String(y), label: String(y) }));

export default function ReportAffittiPage() {
  const { t } = useTranslation();
  const MESI = [
    { value: '01', label: t('months.january') },
    { value: '02', label: t('months.february') },
    { value: '03', label: t('months.march') },
    { value: '04', label: t('months.april') },
    { value: '05', label: t('months.may') },
    { value: '06', label: t('months.june') },
    { value: '07', label: t('months.july') },
    { value: '08', label: t('months.august') },
    { value: '09', label: t('months.september') },
    { value: '10', label: t('months.october') },
    { value: '11', label: t('months.november') },
    { value: '12', label: t('months.december') },
  ];
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  
  // Filtri Anno/Mese (nuovo)
  const [selectedAnno, setSelectedAnno] = useState(String(currentYear));
  const [selectedMese, setSelectedMese] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  
  // Workflow state
  const [filter, setFilter] = useState('tutti'); // tutti, da_pagare, pagati, archiviati
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Filtri per colonna (nuovo)
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    appartamento: "",
    proprietario: "",
    stato: ""
  });
  
  // Variazione edit state - track per scadenza
  const [editingVariazione, setEditingVariazione] = useState({}); // {id: {variazione, note_variazione}}
  const [savingVariazione, setSavingVariazione] = useState(null);
  
  // Dialogs
  const [payDialog, setPayDialog] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [paying, setPaying] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [payNote, setPayNote] = useState('');
  const [archiveNote, setArchiveNote] = useState('');

  const formatCurrency = (val) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("it-IT");
    } catch {
      return dateStr;
    }
  };
  
  // Calcola date inizio/fine dal mese selezionato
  const getDateRange = useCallback(() => {
    const anno = parseInt(selectedAnno);
    const mese = parseInt(selectedMese);
    const dataInizio = `${anno}-${selectedMese}-01`;
    const lastDay = new Date(anno, mese, 0).getDate();
    const dataFine = `${anno}-${selectedMese}-${lastDay}`;
    return { dataInizio, dataFine };
  }, [selectedAnno, selectedMese]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { dataInizio, dataFine } = getDateRange();
      const params = new URLSearchParams();
      params.append("data_inizio", dataInizio);
      params.append("data_fine", dataFine);
      params.append("includi_archiviati", showArchived ? "true" : "false");
      
      const prefix = getApiPrefix();
      const res = await api.get(`${prefix}/reports/affitti?${params.toString()}`);
      setReport(res.data);
      setSelectedIds(new Set());
    } catch (err) {
      handleApiError(err, t("errors.loadReport"));
    } finally {
      setLoading(false);
    }
  }, [getDateRange, showArchived, t]);

  // Fetch automatico al cambio anno/mese
  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    if (filter === 'archiviati' && !showArchived) {
      setShowArchived(true);
    }
  }, [filter, showArchived]);

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    try {
      const dateObj = new Date(dateStr);
      return dateObj < new Date();
    } catch {
      return false;
    }
  };

  // Filtra scadenze (aggiornato con filtri colonna)
  const getFilteredScadenze = () => {
    if (!report?.scadenze) return [];
    return report.scadenze.filter(s => {
      // Filtro workflow
      if (filter === 'da_pagare' && (s.stato !== 'da_pagare' || s.archiviato)) return false;
      if (filter === 'pagati' && (s.stato !== 'pagato' || s.archiviato)) return false;
      if (filter === 'archiviati' && s.archiviato !== true) return false;
      if (filter === 'tutti' && s.archiviato) return false;
      
      // Filtri colonna
      if (columnFilters.appartamento && 
          !s.appartamento?.toLowerCase().includes(columnFilters.appartamento.toLowerCase()) &&
          !s.indirizzo?.toLowerCase().includes(columnFilters.appartamento.toLowerCase())) return false;
      if (columnFilters.proprietario && 
          !s.proprietario?.toLowerCase().includes(columnFilters.proprietario.toLowerCase())) return false;
      if (columnFilters.stato && columnFilters.stato !== s.stato) return false;
      
      return true;
    });
  };

  const filteredScadenze = getFilteredScadenze();

  // Selezione
  const toggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredScadenze.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredScadenze.map(s => s.id)));
    }
  };

  const getSelectedTotal = () => {
    return filteredScadenze
      .filter(s => selectedIds.has(s.id))
      .reduce((sum, s) => sum + (s.totale || 0), 0);
  };

  // Segna come pagato
  const handlePay = async () => {
    if (selectedIds.size === 0) return;
    setPaying(true);
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/affitti/segna-pagato`, {
        ids: Array.from(selectedIds),
        note: payNote
      });
      showSuccess(`${selectedIds.size} affitti segnati come pagati`);
      setPayDialog(false);
      setPayNote('');
      setSelectedIds(new Set());
      fetchReport();
    } catch (err) {
      handleApiError(err, t("errors.operationFailed"));
    } finally {
      setPaying(false);
    }
  };

  // Salva variazione
  const handleSaveVariazione = async (scadId) => {
    const edit = editingVariazione[scadId];
    if (!edit) return;
    
    setSavingVariazione(scadId);
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/affitti/salva-variazione`, {
        id: scadId,
        variazione: parseFloat(edit.variazione) || 0,
        note_variazione: edit.note_variazione || ''
      });
      showSuccess(t("success.variationSaved"));
      // Rimuovi dalla lista di editing
      const newEditing = { ...editingVariazione };
      delete newEditing[scadId];
      setEditingVariazione(newEditing);
      fetchReport();
    } catch (err) {
      handleApiError(err, t("errors.errorSaveVariation"));
    } finally {
      setSavingVariazione(null);
    }
  };

  // Inizia editing variazione
  const startEditVariazione = (scad) => {
    setEditingVariazione({
      ...editingVariazione,
      [scad.id]: {
        variazione: scad.variazione || 0,
        note_variazione: scad.note_variazione || ''
      }
    });
  };

  // Update editing variazione
  const updateEditVariazione = (scadId, field, value) => {
    setEditingVariazione({
      ...editingVariazione,
      [scadId]: {
        ...editingVariazione[scadId],
        [field]: value
      }
    });
  };

  // Check if variazione changed
  const hasVariazioneChanged = (scad) => {
    const edit = editingVariazione[scad.id];
    if (!edit) return false;
    return (parseFloat(edit.variazione) || 0) !== (scad.variazione || 0) ||
           (edit.note_variazione || '') !== (scad.note_variazione || '');
  };

  // Archivia
  const handleArchive = async () => {
    if (selectedIds.size === 0) return;
    setArchiving(true);
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/affitti/archivia`, {
        ids: Array.from(selectedIds),
        note: archiveNote
      });
      showSuccess(`${selectedIds.size} affitti archiviati`);
      setArchiveDialog(false);
      setArchiveNote('');
      setSelectedIds(new Set());
      fetchReport();
    } catch (err) {
      handleApiError(err, t("errors.archiveFailed"));
    } finally {
      setArchiving(false);
    }
  };

  // Annulla pagamento
  const handleCancelPayment = async (id) => {
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/affitti/annulla-pagamento`, { ids: [id] });
      showSuccess(t("success.paymentCancelled"));
      fetchReport();
    } catch (err) {
      handleApiError(err, t("errors.cancelFailed"));
    }
  };

  const totali = report?.totali || {};

  return (
    <div className="space-y-6" data-testid="report-affitti-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("apartments.rentReport")}</h1>
          <p className="text-muted-foreground">Scadenze canoni di locazione con workflow pagato/archiviato</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{t('common.refresh')}</Button>
      </div>

      {/* Filtri Anno/Mese */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t("common.period")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("common.year")}</Label>
              <Select value={selectedAnno} onValueChange={setSelectedAnno}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ANNI.map(a => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('common.month')}</Label>
              <Select value={selectedMese} onValueChange={setSelectedMese}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MESI.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant={showColumnFilters ? "default" : "outline"}
              size="sm"
              onClick={() => setShowColumnFilters(!showColumnFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtri Colonna
            </Button>
            {loading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
          </div>
          
          {/* Filtri per colonna */}
          {showColumnFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Appartamento/Indirizzo</Label>
                <Input
                  value={columnFilters.appartamento}
                  onChange={(e) => setColumnFilters({ ...columnFilters, appartamento: e.target.value })}
                  placeholder="Cerca appartamento..."
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('common.owner')}</Label>
                <Input
                  value={columnFilters.proprietario}
                  onChange={(e) => setColumnFilters({ ...columnFilters, proprietario: e.target.value })}
                  placeholder="Cerca proprietario..."
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("common.status")}</Label>
                <Select 
                  value={columnFilters.stato || "_all"} 
                  onValueChange={(v) => setColumnFilters({ ...columnFilters, stato: v === "_all" ? "" : v })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={t("common.all")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">{t('common.all')}</SelectItem>
                    <SelectItem value="da_pagare">{t('common.toPay')}</SelectItem>
                    <SelectItem value="pagato">{t('invoices.paid')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setColumnFilters({ appartamento: "", proprietario: "", stato: "" })}
                >
                  <X className="h-4 w-4 mr-1" /> Reset filtri
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report Results */}
      {report && (
        <>
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-5">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Building className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">{t('apartments.title')}</p>
                    <p className="text-2xl font-bold">{report.appartamenti || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Clock className="h-8 w-8 text-orange-500" />
                  <div>
                    <p className="text-xs text-orange-600 uppercase">{t('common.toPay')}</p>
                    <p className="text-2xl font-bold text-orange-700">{totali.count_da_pagare || 0}</p>
                    <p className="text-sm font-mono text-orange-600">{formatCurrency(totali.da_pagare)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-xs text-green-600 uppercase">Pagati</p>
                    <p className="text-2xl font-bold text-green-700">{totali.count_pagati || 0}</p>
                    <p className="text-sm font-mono text-green-600">{formatCurrency(totali.pagati)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-gray-200 bg-gray-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Archive className="h-8 w-8 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-600 uppercase">Archiviati</p>
                    <p className="text-2xl font-bold text-gray-700">{totali.count_archiviati || 0}</p>
                    <p className="text-sm font-mono text-gray-600">{formatCurrency(totali.archiviati)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div>
                  <p className="text-xs text-blue-600 uppercase">{t("reports.totalPeriod")}</p>
                  <p className="text-2xl font-bold text-blue-700 font-mono">{formatCurrency(totali.totale)}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Workflow Tabs */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant={filter === 'tutti' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilter('tutti')}
                  >{t('common.all')}</Button>
                  <Button
                    variant={filter === 'da_pagare' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilter('da_pagare')}
                    className="gap-1"
                  >
                    <Clock className="h-4 w-4" />
                    Da Pagare ({totali.count_da_pagare || 0})
                  </Button>
                  <Button
                    variant={filter === 'pagati' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilter('pagati')}
                    className="gap-1"
                  >
                    <Check className="h-4 w-4" />
                    Pagati ({totali.count_pagati || 0})
                  </Button>
                  <Button
                    variant={filter === 'archiviati' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setFilter('archiviati');
                      setShowArchived(true);
                    }}
                    className="gap-1"
                  >
                    <Archive className="h-4 w-4" />
                    Archiviati ({totali.count_archiviati || 0})
                  </Button>
                </div>

                <div className="flex gap-2 flex-wrap">
                  {selectedIds.size > 0 && filter === 'da_pagare' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}>{t('common.deselect')}</Button>
                      <Button size="sm" onClick={() => setPayDialog(true)}>
                        <Check className="h-4 w-4 mr-2" />
                        Segna Pagati ({selectedIds.size})
                      </Button>
                    </>
                  )}
                  {selectedIds.size > 0 && filter === 'pagati' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}>{t('common.deselect')}</Button>
                      <Button size="sm" variant="secondary" onClick={() => setArchiveDialog(true)}>
                        <Archive className="h-4 w-4 mr-2" />
                        Archivia ({selectedIds.size})
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Table */}
          <Card>
            <CardHeader>
              <CardTitle>Dettaglio Scadenze ({filteredScadenze.length})</CardTitle>
              <CardDescription>
                {filter === 'da_pagare' && 'Scadenze in attesa di pagamento - puoi inserire variazioni mensili'}
                {filter === 'pagati' && 'Scadenze pagate - seleziona per archiviare dopo verifica'}
                {filter === 'archiviati' && 'Scadenze archiviate'}
                {filter === 'tutti' && 'Tutte le scadenze non archiviate'}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {filteredScadenze.length > 0 ? (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">
                          <Checkbox
                            checked={selectedIds.size === filteredScadenze.length && filteredScadenze.length > 0}
                            onCheckedChange={toggleSelectAll}
                            disabled={filter === 'archiviati' || filter === 'tutti'}
                          />
                        </TableHead>
                        <TableHead>{t('apartments.title')}</TableHead>
                        <TableHead className="w-[120px]">{t('common.owner')}</TableHead>
                        <TableHead className="w-[180px]">IBAN</TableHead>
                        <TableHead className="w-[100px]">{t('invoices.dueDate')}</TableHead>
                        <TableHead className="text-right w-[90px]">{t('apartments.canone')}</TableHead>
                        <TableHead className="text-right w-[100px]">
                          <div className="flex items-center justify-end gap-1">
                            <PlusCircle className="h-3 w-3 text-green-500" />
                            <MinusCircle className="h-3 w-3 text-red-500" />
                            Variazione
                          </div>
                        </TableHead>
                        <TableHead className="text-right w-[90px]">{t('common.expenses')}</TableHead>
                        <TableHead className="text-right w-[100px]">{t("common.total")}</TableHead>
                        <TableHead className="w-[180px]">Note Variazione</TableHead>
                        <TableHead className="w-[90px]">{t("common.status")}</TableHead>
                        <TableHead className="w-[80px]">{t("common.actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredScadenze.map((s) => {
                        const overdue = isOverdue(s.data_scadenza);
                        const isPagato = s.stato === 'pagato';
                        const isArchiviato = s.archiviato === true;
                        const isEditing = editingVariazione[s.id] !== undefined;
                        const editData = editingVariazione[s.id] || {};
                        const variazioneChanged = hasVariazioneChanged(s);
                        
                        // Calcola totale con variazione editata
                        const currentVariazione = isEditing ? (parseFloat(editData.variazione) || 0) : (s.variazione || 0);
                        const currentTotale = s.canone + (s.spese_condominiali || 0) + currentVariazione;
                        
                        return (
                          <TableRow 
                            key={s.id}
                            className={`
                              ${overdue && !isPagato && !isArchiviato ? "bg-red-50" : ""}
                              ${selectedIds.has(s.id) ? "bg-blue-50" : ""}
                              ${isPagato && !isArchiviato ? "bg-green-50/30" : ""}
                              ${isArchiviato ? "bg-gray-50/50" : ""}
                            `}
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedIds.has(s.id)}
                                onCheckedChange={() => toggleSelection(s.id)}
                                disabled={isArchiviato || filter === 'tutti'}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{s.appartamento}</div>
                              <div className="text-xs text-muted-foreground">{s.indirizzo}</div>
                              {s.inquilino && <div className="text-xs text-blue-600">{s.inquilino}</div>}
                              {s.frequenza_pagamento && s.frequenza_pagamento !== 'mensile' && (
                                <Badge variant="outline" className="text-[10px] mt-0.5">
                                  {s.frequenza_pagamento}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="font-medium text-sm">{s.proprietario || '-'}</div>
                            </TableCell>
                            <TableCell>
                              {s.iban_proprietario ? (
                                <div className="text-xs text-muted-foreground font-mono" title={s.iban_proprietario}>
                                  {s.iban_proprietario}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {overdue && !isPagato && !isArchiviato && (
                                  <AlertTriangle className="h-4 w-4 text-red-500" />
                                )}
                                <span className={overdue && !isPagato && !isArchiviato ? "text-red-600 font-medium" : ""}>
                                  {formatDate(s.data_scadenza)}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(s.canone)}</TableCell>
                            
                            {/* Campo Variazione Editabile */}
                            <TableCell className="p-1">
                              {!isArchiviato ? (
                                <Input
                                  type="number"
                                  step="0.01"
                                  className={`h-8 w-[90px] text-right font-mono text-sm ${
                                    currentVariazione > 0 ? 'text-green-600 bg-green-50' : 
                                    currentVariazione < 0 ? 'text-red-600 bg-red-50' : ''
                                  }`}
                                  value={isEditing ? editData.variazione : (s.variazione || 0)}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    setEditingVariazione(prev => ({
                                      ...prev,
                                      [s.id]: {
                                        variazione: newValue,
                                        note_variazione: prev[s.id]?.note_variazione || s.note_variazione || ''
                                      }
                                    }));
                                  }}
                                  placeholder="0.00"
                                />
                              ) : (
                                <span className={`font-mono text-sm ${
                                  s.variazione > 0 ? 'text-green-600' : s.variazione < 0 ? 'text-red-600' : 'text-muted-foreground'
                                }`}>
                                  {s.variazione ? formatCurrency(s.variazione) : '-'}
                                </span>
                              )}
                            </TableCell>
                            
                            <TableCell className="text-right font-mono text-purple-600">
                              {s.spese_condominiali > 0 ? formatCurrency(s.spese_condominiali) : "-"}
                            </TableCell>
                            <TableCell className={`text-right font-mono font-bold ${
                              currentVariazione !== 0 ? 'text-blue-700' : ''
                            }`}>
                              {formatCurrency(currentTotale)}
                            </TableCell>
                            
                            {/* Campo Note Variazione */}
                            <TableCell className="p-1">
                              {!isArchiviato ? (
                                <div className="flex gap-1">
                                  <Input
                                    className="h-8 text-xs flex-1"
                                    value={isEditing ? editData.note_variazione : (s.note_variazione || '')}
                                    onChange={(e) => {
                                      const newValue = e.target.value;
                                      setEditingVariazione(prev => ({
                                        ...prev,
                                        [s.id]: {
                                          variazione: prev[s.id]?.variazione ?? (s.variazione || 0),
                                          note_variazione: newValue
                                        }
                                      }));
                                    }}
                                    placeholder="Motivo variazione..."
                                  />
                                  {variazioneChanged && (
                                    <Button
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() => handleSaveVariazione(s.id)}
                                      disabled={savingVariazione === s.id}
                                    >
                                      {savingVariazione === s.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Save className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  {s.note_variazione || '-'}
                                </span>
                              )}
                            </TableCell>
                            
                            <TableCell>
                              {isArchiviato ? (
                                <Badge className="bg-gray-100 text-gray-600 border-0 text-xs">
                                  <Archive className="h-3 w-3 mr-1" />
                                  Arch.
                                </Badge>
                              ) : isPagato ? (
                                <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                                  <Check className="h-3 w-3 mr-1" />{t('invoices.paid')}</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Attesa
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {isPagato && !isArchiviato && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCancelPayment(s.id)}
                                  title={t("treasury.cancelPayment")}
                                >
                                  <X className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                              {isArchiviato && s.archiviato_il && (
                                <span className="text-xs text-muted-foreground">
                                  {formatDate(s.archiviato_il)}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Home className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>
                    {filter === 'archiviati' ? 'Nessuna scadenza archiviata' : 
                     filter === 'pagati' ? 'Nessuna scadenza pagata' :
                     filter === 'da_pagare' ? 'Nessuna scadenza da pagare' :
                     'Nessuna scadenza trovata'}
                  </p>
                  <p className="text-sm mt-2">
                    {!report?.scadenze?.length && 'Clicca "Genera Report" per caricare i dati'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Dialog Segna come Pagato */}
      <Dialog open={payDialog} onOpenChange={setPayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              Segna come Pagati
            </DialogTitle>
            <DialogDescription>
              Segna {selectedIds.size} affitti come pagati.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-700">Scadenze selezionate:</span>
                <span className="font-bold text-green-800">{selectedIds.size}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-green-700">Importo totale:</span>
                <span className="font-bold text-green-800">{formatCurrency(getSelectedTotal())}</span>
              </div>
            </div>
            
            <div className="space-y-1.5">
              <Label>Note (opzionale)</Label>
              <Input
                value={payNote}
                onChange={(e) => setPayNote(e.target.value)}
                placeholder="Es. Bonifico del 15/01..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handlePay} disabled={paying}>
              {paying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Conferma Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Archivia */}
      <Dialog open={archiveDialog} onOpenChange={setArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-gray-600" />
              Archivia Affitti
            </DialogTitle>
            <DialogDescription>
              Archivia {selectedIds.size} affitti pagati dopo la seconda verifica.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">Affitti da archiviare:</span>
                <span className="font-bold text-gray-800">{selectedIds.size}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-gray-700">Importo totale:</span>
                <span className="font-bold text-gray-800">{formatCurrency(getSelectedTotal())}</span>
              </div>
            </div>
            
            <div className="space-y-1.5">
              <Label>Note archiviazione (opzionale)</Label>
              <Input
                value={archiveNote}
                onChange={(e) => setArchiveNote(e.target.value)}
                placeholder="Es. Verificato da..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveDialog(false)}>{t("common.cancel")}</Button>
            <Button variant="secondary" onClick={handleArchive} disabled={archiving}>
              {archiving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Archive className="h-4 w-4 mr-2" />}
              Archivia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
